import { useRef, useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useGame } from '../state/GameContext.jsx';
import { useNoScroll } from '../hooks/useNoScroll.js';
import DrawingCanvas from '../components/DrawingCanvas.jsx';
import RoundResultView from '../components/RoundResultView.jsx';
import FinalView from '../components/FinalView.jsx';

export default function PlayerFlowScreen() {
  useNoScroll();
  const [, setLocation] = useLocation();
  const { state, disconnect } = useGame();
  const { room, wsStatus, participantId } = state;

  if (wsStatus === 'disconnected' && !room) {
    return (
      <div className="min-h-screen tv-bg-stripe flex flex-col items-center justify-center gap-4 p-6">
        <p className="font-body text-tv-ash">接続が切れました</p>
        <button className="tv-btn tv-btn-white" onClick={() => { disconnect(); setLocation('/'); }}>
          トップへ
        </button>
      </div>
    );
  }
  if (!room) {
    return (
      <div className="min-h-screen tv-bg-stripe flex items-center justify-center">
        <p className="font-body text-tv-ash animate-pulse">接続中…</p>
      </div>
    );
  }

  const me = room.participants?.find((p) => p.id === participantId);

  if (room.status === 'lobby') return <PlayerWaiting room={room} me={me} />;
  if (room.status === 'playing') return <PlayerAnswer room={room} me={me} participantId={participantId} />;
  if (room.status === 'scoring') return <PlayerWaitingScore me={me} />;
  if (room.status === 'roundResult') return <RoundResultView isHost={false} />;
  if (room.status === 'final') return <FinalView room={room} />;

  return (
    <div className="min-h-screen tv-bg-stripe flex items-center justify-center">
      <p className="font-body text-tv-ash animate-pulse">待機中…</p>
    </div>
  );
}

// ── Waiting（ロビー）──────────────────────────────────────
function PlayerWaiting({ room, me }) {
  return (
    <div className="min-h-screen tv-bg-stripe flex flex-col items-center justify-center gap-6 p-6">
      <h2 className="tv-heading text-3xl">それいけ採点マン</h2>

      <div className="tv-panel p-5 flex flex-col items-center gap-2 w-full max-w-xs">
        <span className="text-5xl">{me?.emoji}</span>
        <p className="font-body font-bold text-lg">{me?.nickname}</p>
        <span className="tv-telop text-xs">参加中</span>
      </div>

      <div className="tv-panel-sm p-4 w-full max-w-xs">
        <p className="font-body text-sm text-tv-ash mb-2 text-center">
          参加者 ({room.participants.filter((p) => p.connected).length}人)
        </p>
        <div className="flex flex-wrap gap-2 justify-center">
          {room.participants.filter((p) => p.connected).map((p) => (
            <div key={p.id} className="flex items-center gap-1 tv-panel-sm px-2 py-1">
              <span>{p.emoji}</span>
              <span className="font-body text-xs">{p.nickname}</span>
            </div>
          ))}
        </div>
      </div>

      <p className="font-body text-tv-ash text-sm animate-pulse">
        ホストのゲーム開始を待っています…
      </p>
    </div>
  );
}

// ── Answer（回答）──────────────────────────────────────────
function PlayerAnswer({ room, me, participantId }) {
  const { send } = useGame();
  const canvasRef = useRef(null);
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const TIME_LIMIT = 60;
  const prompt = room.currentPrompt;
  const isAlreadySubmitted = Boolean((room.currentAnswers ?? {})[participantId]);

  useEffect(() => {
    if (isAlreadySubmitted) { setSubmitted(true); return; }
    const timer = setInterval(() => {
      setElapsed((e) => {
        if (e + 1 >= TIME_LIMIT && !submitted) {
          // 自動送信
          submitAnswer(true);
        }
        return e + 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isAlreadySubmitted]); // eslint-disable-line

  const submitAnswer = (auto = false) => {
    if (submitted) return;
    setSubmitted(true);
    if (prompt?.type === 'draw') {
      const dataUrl = canvasRef.current?.getDataUrl() ?? '';
      send({ type: 'player:submit', answerType: 'draw', dataUrl });
    } else {
      send({ type: 'player:submit', answerType: 'text', text: auto ? '' : text.trim() });
    }
  };

  const remaining = Math.max(0, TIME_LIMIT - elapsed);
  const isUrgent = remaining <= 10;

  if (submitted || isAlreadySubmitted) {
    return (
      <div className="min-h-screen tv-bg-stripe flex flex-col items-center justify-center gap-4 p-6">
        <span className="text-6xl">✅</span>
        <p className="tv-heading text-2xl">送信完了！</p>
        <p className="font-body text-tv-ash text-sm">ホストの採点を待っています…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen tv-bg-stripe flex flex-col items-center p-4 gap-4 overflow-y-auto">
      <div className="w-full max-w-sm flex items-center justify-between mt-2">
        <div className="tv-panel-sm px-3 py-1 flex items-center gap-2">
          <span>{me?.emoji}</span>
          <span className="font-body text-sm">{me?.nickname}</span>
        </div>
        <div className={`font-num text-3xl font-bold ${isUrgent ? 'text-tv-red animate-pulse-danger' : 'text-tv-blue'}`}>
          {remaining}
        </div>
      </div>

      <div className="tv-panel w-full max-w-sm p-4 flex flex-col items-center gap-2">
        <p className="font-body text-xs text-tv-ash">お題</p>
        <p className="tv-heading text-2xl text-center">{prompt?.text}</p>
        <span className="tv-telop text-xs">
          {prompt?.type === 'draw' ? '🖊 お絵かきで答えよ' : '📝 テキストで答えよ'}
        </span>
      </div>

      <div className="w-full max-w-sm">
        {prompt?.type === 'draw' ? (
          <DrawingCanvas ref={canvasRef} disabled={submitted} />
        ) : (
          <textarea
            className="w-full border-4 border-tv-sumi rounded-tv p-3 font-body text-base resize-none focus:outline-none focus:border-tv-yellow"
            rows={4}
            maxLength={100}
            placeholder="ここに回答を入力…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={submitted}
          />
        )}
      </div>

      <button
        className="tv-btn tv-btn-red text-xl py-3 px-10 w-full max-w-sm"
        onClick={() => submitAnswer(false)}
        disabled={submitted}
      >
        送信！
      </button>
    </div>
  );
}

// ── WaitingScore（採点待ち）──────────────────────────────
function PlayerWaitingScore({ me }) {
  return (
    <div className="min-h-screen tv-bg-stripe flex flex-col items-center justify-center gap-6 p-6">
      <span className="text-6xl">{me?.emoji}</span>
      <p className="tv-heading text-2xl">回答完了！</p>
      <p className="font-body text-tv-ash text-sm animate-pulse">
        ホストが採点中…
      </p>
      <div className="flex gap-1 text-3xl animate-bounce">
        <span>⭐</span><span>⭐</span><span>⭐</span>
      </div>
    </div>
  );
}
